import { CreateReference, LocationTypeCode, getReferenceId } from '@quromedical/fhir-common';
import {
  AdmissionLocationUpdateRequest,
  admissionLocationUpdateRequestSchema,
} from '@quromedical/models';
import { CrudForm, SubmissionResult } from 'components/form';
import { Field, SelectOption } from 'components/types';
import { FetcherSearchOptions } from 'design-system';
import { FormikErrors } from 'formik';
import React, { useCallback, useMemo } from 'react';
import { strings } from 'strings';

type LocationFetcher = (
  search?: FetcherSearchOptions<{ type?: LocationTypeCode }>
) => Promise<SelectOption[]>;

interface AdmissionLocationFormProps {
  initialLocation?: CreateReference;
  showErrors?: boolean;
  onSubmit?: (data: AdmissionLocationUpdateRequest) => Promise<SubmissionResult<unknown>>;
  onValidChange?: (data?: AdmissionLocationUpdateRequest) => void;
  onChange?: (
    data: Partial<AdmissionLocationUpdateRequest>,
    errors: FormikErrors<AdmissionLocationUpdateRequest>,
    hasErrors: boolean
  ) => void;
  onCancel?: () => void;
  locationFetcher: LocationFetcher;
}

const createFields = (
  locationFetcher: LocationFetcher
): Field<AdmissionLocationUpdateRequest>[] => [
  {
    subfields: [
      {
        key: 'location',
        label: strings.Location,
        type: 'combobox-single',
        searchable: true,
        fetcher: locationFetcher,
      },
    ],
  },
];

export const AdmissionLocationForm: React.FC<AdmissionLocationFormProps> = ({
  initialLocation,
  showErrors,
  onValidChange,
  onSubmit,
  onChange,
  onCancel,
  locationFetcher,
}) => {
  const fields = useMemo(() => createFields(locationFetcher), [locationFetcher]);

  const handleChange = useCallback(
    (
      data: Partial<AdmissionLocationUpdateRequest>,
      errors: FormikErrors<AdmissionLocationUpdateRequest>,
      hasErrors: boolean
    ) => {
      if (onChange) {
        onChange(data, errors, hasErrors);
      }

      if (onValidChange) {
        if (hasErrors) {
          onValidChange(undefined);
        } else {
          onValidChange(data as AdmissionLocationUpdateRequest);
        }
      }
    },
    [onChange, onValidChange]
  );
  const initialData = {
    location: getReferenceId(initialLocation),
  };

  return (
    <CrudForm<AdmissionLocationUpdateRequest>
      title={strings.Location}
      validationSchema={admissionLocationUpdateRequestSchema}
      fields={fields}
      initialValues={initialData}
      onSubmit={onSubmit}
      onChange={handleChange}
      onChangeDebounceTime={0}
      onSecondarySubmit={onCancel}
      showSecondarySubmitButton={!!onCancel}
      showSubmitButton={!!onSubmit}
      showErrors={showErrors}
      validateOnChange
      validateOnMount
    />
  );
};
